import { Attachment, IAttachment } from '@app/models/Attachment'
import { IListing, Listing } from '@app/models/Listing'
import { IShareContact } from '@app/models/ShareContact'
import { ApplicationState } from '@app/store'
import { humanize } from '@app/utils/formatting'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, take, takeEvery } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum ListingShareType {
  Flyer = 'Flyer',
  MarketingPackage = 'MarketingPackage',
}

export interface IShareAttachmentState {
  readonly attachment?: IAttachment
  readonly isSaving: boolean
  readonly listing?: IListing
  readonly listingShareType?: ListingShareType
  readonly propertyId?: string
}

const ShareAttachmentStateRecord = Record({
  attachment: undefined,
  isSaving: false,
  listing: undefined,
  listingShareType: undefined,
  propertyId: undefined,
})

export class ShareAttachmentState extends ShareAttachmentStateRecord
  implements IShareAttachmentState {
  public static fromJS(initialState?: Partial<IShareAttachmentState>) {
    if (!initialState) {
      return new ShareAttachmentState()
    }

    const { attachment, listing, ...rest } = initialState

    return new ShareAttachmentState({
      attachment: attachment ? Attachment.fromJS(attachment) : undefined,
      listing: listing ? Listing.fromJS(listing) : undefined,
      ...rest,
    })
  }

  public readonly attachment?: Attachment
  public readonly isSaving: boolean
  public readonly listing?: Listing
  public readonly listingShareType?: ListingShareType
  public readonly propertyId?: string

  constructor(props?: Partial<ShareAttachmentState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  CloseShareAttachmentModal = 'SHARE_ATTACHMENT::CLOSE_SHARE_ATTACHMENT_MODAL',
  ResetState = 'SHARE_ATTACHMENT::RESET_STATE',
  ShareAttachment = 'SHARE_ATTACHMENT::SHARE_ATTACHMENT',
  ShareAttachmentFailure = 'SHARE_ATTACHMENT::SHARE_ATTACHMENT_FAILURE',
  ShareAttachmentRequest = 'SHARE_ATTACHMENT::SHARE_ATTACHMENT_REQUEST',
  ShareAttachmentSuccess = 'SHARE_ATTACHMENT::SHARE_ATTACHMENT_SUCCESS',
  ShowShareAttachmentModal = 'SHARE_ATTACHMENT::SHOW_SHARE_ATTACHMENT_MODAL',
  ShowShareListingAttachmentModal = 'SHARE_ATTACHMENT::SHOW_SHARE_LISTING_ATTACHMENT_MODAL',
}

interface ICloseShareAttachmentModalAction {
  type: ActionTypes.CloseShareAttachmentModal
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface IShareAttachmentAction {
  comment: string
  contacts: List<IShareContact>
  subject: string
  type: ActionTypes.ShareAttachment
}

interface IShareAttachmentFailureAction {
  type: ActionTypes.ShareAttachmentFailure
}

interface IShareAttachmentRequestAction {
  type: ActionTypes.ShareAttachmentRequest
}

interface IShareAttachmentSuccessAction {
  type: ActionTypes.ShareAttachmentSuccess
}

interface IShowShareAttachmentModalAction {
  attachment: IAttachment
  propertyId?: string
  type: ActionTypes.ShowShareAttachmentModal
}

interface IShowShareListingAttachmentModalAction {
  attachment: IAttachment
  listing: IListing
  listingShareType: ListingShareType
  type: ActionTypes.ShowShareListingAttachmentModal
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICloseShareAttachmentModalAction
  | IResetStateAction
  | IShareAttachmentFailureAction
  | IShareAttachmentRequestAction
  | IShareAttachmentSuccessAction
  | IShowShareAttachmentModalAction
  | IShowShareListingAttachmentModalAction

// -------------------------
// ACTION CREATORS
// -------------------------
function closeShareAttachmentModal(): ICloseShareAttachmentModalAction {
  return { type: ActionTypes.CloseShareAttachmentModal }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function shareAttachment(
  contacts: List<IShareContact>,
  subject: string,
  comment: string,
): IShareAttachmentAction {
  return {
    comment,
    contacts,
    subject,
    type: ActionTypes.ShareAttachment,
  }
}

function shareAttachmentFailure(): IShareAttachmentFailureAction {
  return { type: ActionTypes.ShareAttachmentFailure }
}

function shareAttachmentRequest(): IShareAttachmentRequestAction {
  return { type: ActionTypes.ShareAttachmentRequest }
}

function shareAttachmentSuccess(): IShareAttachmentSuccessAction {
  return { type: ActionTypes.ShareAttachmentSuccess }
}

function showShareAttachmentModal(
  attachment: IAttachment,
  propertyId?: string,
): IShowShareAttachmentModalAction {
  return {
    attachment,
    propertyId,
    type: ActionTypes.ShowShareAttachmentModal,
  }
}

function showShareListingAttachmentModal(
  attachment: IAttachment,
  listing: IListing,
  listingShareType: ListingShareType,
): IShowShareListingAttachmentModalAction {
  return {
    attachment,
    listing,
    listingShareType,
    type: ActionTypes.ShowShareListingAttachmentModal,
  }
}

export const actionCreators = {
  closeShareAttachmentModal,
  resetState,
  shareAttachment,
}

export const shareAttachmentActionCreators = {
  showShareAttachmentModal,
  showShareListingAttachmentModal,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ShareAttachmentState> = (
  state: ShareAttachmentState = new ShareAttachmentState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.CloseShareAttachmentModal:
      return new ShareAttachmentState()

    case ActionTypes.ResetState:
      return new ShareAttachmentState()

    case ActionTypes.ShareAttachmentFailure:
      return state.set('isSaving', false) as ShareAttachmentState

    case ActionTypes.ShareAttachmentRequest:
      return state.set('isSaving', true) as ShareAttachmentState

    case ActionTypes.ShareAttachmentSuccess:
      return state.set('isSaving', false) as ShareAttachmentState

    case ActionTypes.ShowShareAttachmentModal:
      return state
        .set('attachment', action.attachment as Attachment)
        .set('propertyId', action.propertyId) as ShareAttachmentState

    case ActionTypes.ShowShareListingAttachmentModal:
      return state
        .set('attachment', action.attachment as Attachment)
        .set('listing', action.listing as Listing)
        .set(
          'listingShareType',
          action.listingShareType,
        ) as ShareAttachmentState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* shareAttachmentSaga(action: IShareAttachmentAction) {
  try {
    yield put(shareAttachmentRequest())

    const state: ShareAttachmentState = yield select(selectModuleState())

    if (!state.attachment) {
      return null
    }

    const contactEmails = action.contacts
      .map((item: any) => {
        return item.addresses
      })
      .reduce((pre: any, current: any) => {
        return pre.concat(current)
      })

    const shareUrl = state.listing
      ? `/api/Listings/${state.listing.id}/Share`
      : `/api/Files/${state.attachment.id}/Share`

    const shareOpts = {
      body: JSON.stringify({
        comment: action.comment,
        contacts: contactEmails.toJS(),
        isFlyer: state.listingShareType === ListingShareType.Flyer,
        isMarketingPackage:
          state.listingShareType === ListingShareType.MarketingPackage,
        subject: action.subject,
      }),
      method: RequestMethod.POST,
    }

    const shareResult = yield makeAuthenticatedRequest(shareUrl, shareOpts)

    if (shareResult.success === true) {
      yield put(shareAttachmentSuccess())
      yield put(resetState())

      const attachmentName =
        state.attachment.title || state.attachment.fileName

      yield put(
        successToast('Share Attachment', `${attachmentName} has been shared`),
      )

      if (state.propertyId || state.listing) {
        const postUrl = state.listing
          ? '/api/Listings/Share/Post'
          : '/api/Properties/Share/Post'
        const postOpts = {
          body: JSON.stringify({
            attachmentType: humanize(
              state.listingShareType || state.attachment.type,
            ),
            contacts: contactEmails.toJS(),
            fileId: state.attachment.id,
            property: state.listing ? state.listing.property.toJS() : undefined,
            propertyId: state.propertyId,
          }),
          method: RequestMethod.POST,
        }

        const postResult = yield makeAuthenticatedRequest(postUrl, postOpts)

        if (postResult.success === false) {
          const msg = `${attachmentName} was shared but we were unabled to create the post on the property: ${
            postResult.message
          }`
          yield put(errorToast('Share Attachment Activity Failed', msg))
        }
      }
    } else {
      yield put(shareAttachmentFailure())
      yield put(errorToast('Share Attachment Failed', shareResult.message))
    }
  } catch (err) {
    yield put(shareAttachmentFailure())
    yield put(errorToast('Share Attachment Failed', err.message))
  }
}

function* watchShareAttachment() {
  yield takeEvery(ActionTypes.ShareAttachment, shareAttachmentSaga)
}

export function* shareAttachmentRootSaga() {
  yield fork(watchShareAttachment)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.shareAttachment

export const selectAttachment = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.attachment,
  )

export const selectIsSaving = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isSaving,
  )

export const selectListing = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.listing,
  )

export const selectListingShareType = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.listingShareType,
  )

export const selectPropertyId = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.propertyId,
  )
